import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import type { AssignmentTypeValue } from "./schedules";
import { BracketTypeConstraint } from "../Brackets/configurationTemplates";
import { BracketSeedCompetitor, BracketSeedingType, BracketType, BracketTypeEnum, Mat } from "../Common/types";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { ComplexBracket } from "../Common/Brackets/types";
import type { BracketMatch } from "../Common/Fights/types";
import type { KataEntry } from "../Common/Kata/useKataScores";

export type BracketTypeConstraintWithTranslationKey = BracketTypeConstraint & {
    name: string;
    settings: Record<string, unknown>; //TODO Will be used in the future.
};

export type BracketDefaultsConfigurationResponse = {
    bracketTypeConstraints: BracketTypeConstraint[];
    configurationOptions: ConfigurationOptions;
    bracketsTypeConstraintsSaved: boolean;
};

export const CompetitorsExcludeStrategy = {
    ExcludeMissingPayments: "excludeMissingPayments",
    ExcludeMissingPaymentsAndDisqualified: "excludeMissingPaymentsAndDisqualified",
    NoExclusion: "noExclusion",
} as const;

export type CompetitorsExcludeStrategyType = (typeof CompetitorsExcludeStrategy)[keyof typeof CompetitorsExcludeStrategy];

export type ConfigurationOptions = {
    competitorsExcludeStrategy: CompetitorsExcludeStrategyType;
    enableBracketsWithSingleCompetitor: boolean;
};

export type DefaultsConfigurationUpdate = {
    bracketTypeConstraints: BracketTypeConstraint[];
    configurationOptions: ConfigurationOptions;
};

export type AutoSeedingPreview = {
    parameterId: number;
    categoryName: string;
    status: "new" | "update" | "delete";
    targetBracketType: number;
    blocked: boolean;
    blockReason: string | null;
    outdatedReasons: string[];
    size: number;
};

export type AutoSeedingPreviewResponse = AutoSeedingPreview[];

//Source: Panel\Brackets\Sharing\BracketsSharingState
export type BracketsSharingState = {
    enabled: boolean;
    publicUrl: string;
};

//Source: Panel\Brackets\Sharing\UpdateBracketsSharingRequest
export type UpdateBracketsSharingRequest = {
    enabled: boolean;
};

export type MedalCalculationResponse = Record<"1" | "2" | "3", number>;

export const MedalCalculationStrategy = {
    AllRegistrations: "allRegistrations",
    BracketsConfiguration: "bracketsConfiguration",
};
export type MedalCalculationStrategyType = (typeof MedalCalculationStrategy)[keyof typeof MedalCalculationStrategy];

export type RankingMetadataEntry = {
    registrationId: number;
    score: number | null;
    protected: boolean;
    firstName: string;
    lastName: string;
    academy: string;
    photoUrl: string | null;
};

// Mirrors \Panel\PublicBracket\Model\BracketRoundName::jsonSerialize().
export type BracketRoundName = {
    translationKey?: string;
    label?: string;
};

// Mirrors one entry of \Panel\PublicBracket\Model\BracketGames::jsonSerialize().
export type BracketRound = {
    games: BracketMatch[];
    name?: BracketRoundName;
};

// Mirrors \Panel\PublicBracket\Model\BracketMatchWithSuccessor::jsonSerialize() — a bout
// carrying its own id and the id of the fight its winner advances to (null for a terminal bout).
export type BracketSuccessorMatch = BracketMatch & {
    id: number;
    next: number | null;
};

// Mirrors \Panel\Schedule\SharingManagement\Model\ScheduledBracketSegment::jsonSerialize(). `start`/`end` are UTC.
export type PublicBracketScheduleSegment = {
    scheduleId: number;
    matName: string;
    start: string;
    end: string;
    roundMarkerFrom: string | null;
    roundMarkerTo: string | null;
    // AssignmentTypeValue: 1 = whole bracket, otherwise half/quarter (see Api/schedules.ts)
    assignmentType: AssignmentTypeValue;
};

// Mirrors \Panel\PublicBracket\Schedule\PublicBracketSchedule::jsonSerialize().
export type PublicBracketSchedule = {
    // Event-wide: true when scheduled categories start on at least two different days (event timezone).
    showDayName: boolean;
    segments: PublicBracketScheduleSegment[];
};

type PublicBracketBase = {
    category: string;
    totalCompetitors: number;
    typeKey: string;
    totalFights: number;
    fightTime: number | null;
    selectedMats: Array<Mat>;
    seedingType: BracketSeedingType;
    manualChange: boolean;
    createdAt: string;
    metadata: { ranking: RankingMetadataEntry[] } | null;
    schedule: PublicBracketSchedule;
};

// Formats whose `results` is a single list of rounds.
type RoundBasedBracketType =
    | typeof BracketTypeEnum.SingleElimination
    | typeof BracketTypeEnum.SingleElimination3rdPlacePlayoff
    | typeof BracketTypeEnum.RoundRobin
    | typeof BracketTypeEnum.BestOfThree
    | typeof BracketTypeEnum.ThreeCompetitorRepechage;

// Formats whose `results` is [winners rounds, defeated/repechage successor ladder].
type DoubleEliminationBracketType =
    | typeof BracketTypeEnum.DoubleEliminationOne3rdPlace
    | typeof BracketTypeEnum.DoubleEliminationTwo3rdPlaces
    | typeof BracketTypeEnum.RepechageByFinalists;

// Formats whose `results` is 2-4 round-list sections (two pools then one or two finals sections).
type TwoPoolsBracketType = typeof BracketTypeEnum.TwoPoolsOne3rdPlace | typeof BracketTypeEnum.TwoPoolsTwo3rdPlaces;

// `results` is a positional array whose entry shapes depend on `type` — see the per-strategy
// providers under \Panel\PublicBracket\Strategy. Discriminating on `type` lets callers read
// `results` without casting.
export type PublicBracket = PublicBracketBase &
    (
        | { type: RoundBasedBracketType; results: [BracketRound[]] }
        | { type: DoubleEliminationBracketType; results: [BracketRound[], BracketSuccessorMatch[]] }
        | { type: TwoPoolsBracketType; results: BracketRound[][] }
        | { type: typeof BracketTypeEnum.Kata; results: [KataEntry[]] }
    );

export type BracketTypeJsonView = {
    id: BracketType;
    key: string;
};

export type SupportedBracketTypeResponse = BracketTypeJsonView[];

export type SkippedBracketDelete = {
    parameterId: number;
    reason: string;
};

export type BulkDeleteBracketsResult = {
    deletedParameterIds: number[];
    skipped: SkippedBracketDelete[];
};

export type BracketPersistRequest = {
    eventId: number;
    parameterId: number;
    type: BracketType;
    competitors: Array<number>;
    seedingType: BracketSeedingType;
    manualChange: boolean;
};

export default {
    getSupportedBracketTypes(eventId: number, parameterId: number): Promise<SupportedBracketTypeResponse> {
        return promiseRequest<SupportedBracketTypeResponse>({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}/supported-types`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getBracket(eventId: number, parameterId: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    persistBracket(request: BracketPersistRequest) {
        return promiseRequest({
            url: `/api/admin/events/${request.eventId}/brackets/${request.parameterId}`,
            method: "PUT",
            json: {
                parameterId: request.parameterId,
                eventId: request.eventId,
                type: request.type,
                competitors: request.competitors,
                seedingType: request.seedingType,
                manualChange: request.manualChange ?? false,
            },
        }).then(resolveNoContent());
    },
    generateBracketSeeding(
        eventId: number,
        parameterId: number,
        bracketType: BracketType,
        seedingType: BracketSeedingType,
    ): Promise<BracketSeedCompetitor[]> {
        return promiseRequest<BracketSeedCompetitor[]>({
            url: `/api/admin/events/${eventId}/brackets`,
            method: "post",
            json: {
                parameterId: Number(parameterId),
                bracketType: Number(bracketType),
                seedingType,
            },
        }).then(resolveWithBody());
    },
    getComplexBrackets(eventId: number, includeEmptyCategories: boolean = false): Promise<ComplexBracket[]> {
        let url = `/api/admin/events/${eventId}/complex-brackets`;

        if (includeEmptyCategories) {
            url += "?includeEmptyCategories=1";
        }

        return promiseRequest<ComplexBracket[]>({
            url: url,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    deleteBracket(eventId: number, parameterId: number): Promise<void> {
        return promiseRequest<void>({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    bulkDeleteBrackets(eventId: number, parameterIds: number[]): Promise<BulkDeleteBracketsResult> {
        return promiseRequest<BulkDeleteBracketsResult>({
            url: `/api/admin/events/${eventId}/brackets/actions/bulk-delete`,
            method: "post",
            json: { parameterIds },
        }).then(resolveWithBody());
    },
    getBracketsSharing(eventId: number): Promise<BracketsSharingState> {
        return promiseRequest<BracketsSharingState>({
            url: `/api/admin/events/${eventId}/brackets-sharing`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    updateBracketsSharing(eventId: number, request: UpdateBracketsSharingRequest): Promise<BracketsSharingState> {
        return promiseRequest<BracketsSharingState>({
            url: `/api/admin/events/${eventId}/brackets-sharing`,
            method: "put",
            json: request,
        }).then(resolveWithBody());
    },
    getBracketTypes(eventId: number): Promise<BracketTypeConstraintWithTranslationKey[]> {
        return promiseRequest<BracketTypeConstraintWithTranslationKey[]>({
            url: `/api/admin/events/${eventId}/brackets/types`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getBracketDefaultsConfiguration(eventId: number): Promise<BracketDefaultsConfigurationResponse> {
        return promiseRequest<BracketDefaultsConfigurationResponse>({
            url: `/api/admin/events/${eventId}/brackets/defaults-configuration`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    updateBracketDefaultsConfiguration(eventId: number, request: DefaultsConfigurationUpdate): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/defaults-configuration`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },
    getAutoSeedingPreview(
        eventId: number,
        seedingType: string,
        includeManualBrackets: boolean = false,
    ): Promise<AutoSeedingPreviewResponse> {
        const params = new URLSearchParams({ seedingType });
        if (includeManualBrackets) {
            params.set("includeManualBrackets", "1");
        }
        return promiseRequest<AutoSeedingPreviewResponse>({
            url: `/api/admin/events/${eventId}/brackets/auto-seeding?${params.toString()}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    autoSeedBrackets(eventId: number, seedingType: string, includeManualBrackets: boolean = false, parameterIds: number[] = []) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/auto-seeding`,
            method: "put",
            json: {
                eventId,
                seedingType,
                includeManualBrackets,
                parameterIds,
            },
        }).then(res => {
            if (res.statusCode !== 204) {
                throw Error("Could not auto seed brackets.");
            }
        });
    },
    calculateMedalPlaces(eventId: number, type: MedalCalculationStrategyType): Promise<MedalCalculationResponse> {
        return promiseRequest<MedalCalculationResponse>({
            url: `/api/admin/events/${eventId}/medal-places?type=${type}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getMissingCompetitorsFromBracket(eventId, parameterId) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}/missing-competitors`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    replaceCompetitorInBracket(eventId, parameterId, eventRegistrationId, indexInBracket) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/brackets/${parameterId}/replace-competitor`,
            method: "put",
            json: {
                parameterId: parameterId,
                eventRegistrationId: eventRegistrationId,
                indexInBracket: indexInBracket,
            },
        }).then(resolveNoContent());
    },
    getPublicBracketData(eventId: number, parameterId: number): Promise<PublicBracket> {
        return promiseRequest<PublicBracket>({
            url: `/api/events/${eventId}/results/public/bracket/${parameterId}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};
